import { Ref, useCallback, useMemo } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { AttachArticles } from "../../../AttachArticles/AttachArticles";
import { useItemsInfoQueries } from "@coworker/apprestructured/src/shared/hooks/item/useItemsInfo";
import { Item } from "@coworker/apprestructured/src/shared/types/item";

export type AttachArticlesCreatorFragmentConfigProps = {
  field: string;
};

type AttachArticlesCreatorFragmentProps = {
  configProps: AttachArticlesCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function AttachArticlesCreatorFragment({
  configProps,
  commonProps,
}: AttachArticlesCreatorFragmentProps) {
  const { field } = configProps;
  const { blockInput, task, onTaskChange } = commonProps;
  const itemNos = getByPath<string[]>(task, field);

  const itemQueries = useItemsInfoQueries(isLoading(itemNos) ? [] : itemNos);

  const isFetchingItems = useMemo(() => {
    return itemQueries.some(({ isFetching }) => isFetching);
  }, [itemQueries]);

  const items = useMemo(() => {
    return itemQueries.map(({ data }) => data).filter(Boolean) as Item[];
  }, [itemQueries]);

  const handleConfirmClick = useCallback(
    (newItemNos: string[]) => {
      onTaskChange(setByPath(field, newItemNos));
    },
    [field, onTaskChange]
  );

  if (isLoading(itemNos)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <AttachArticles
      items={items}
      setItemNos={handleConfirmClick}
      disabled={blockInput || isFetchingItems}
    />
  );
}

export default AttachArticlesCreatorFragment;
